import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Building2, CreditCard, Palette, Link2, CheckCircle2,
  Check, ChevronRight, ChevronLeft, Loader2, AlertTriangle,
  Upload, Sparkles, RefreshCw,
} from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/Badge';
import { useAuth } from '@/store/auth';

/**
 * RegisterAgencyPage — P1.5 of BDOS 2.0 (spec 3.5.1).
 *
 * 5-step onboarding wizard for brand-new agency tenants:
 *   1. Konto     — name, slug, email, password, terms
 *   2. Plan      — pick plan card (Basic / Pro / Agency), 14-day trial
 *   3. Branding  — logo, brand color, locale, timezone
 *   4. Google Ads OAuth — connect + pick customer IDs to import
 *   5. Podsumowanie — confirm, redirect to dashboard or first account
 *
 * Auth: step 1 POSTs /onboarding/register and stores the returned Sanctum
 * token directly in localStorage so subsequent authed requests (plan,
 * branding, oauth, import, complete) go through the existing Axios
 * interceptor in lib/api.ts.
 *
 * OAuth flow: step 4 calls /onboarding/oauth-url, redirects to Google.
 * After Google bounces us back to the backend callback, the backend caches
 * the refresh_token + list of accessible customers and redirects the
 * browser to /register?step=4&oauth=success. The page then polls
 * /onboarding/oauth-status every 2s until `connected=true`.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Plan {
  id: number;
  slug: string;
  name: string;
  price_monthly: number;
  currency: string;
  max_accounts: number | null;
  max_users: number | null;
  max_claude_tokens_per_month: number | null;
  features: string[];
}

interface OauthStatus {
  connected: boolean;
  connected_at?: string | null;
  accessible_customers: { customer_id: string; resource_name: string }[];
}

interface Agency {
  id: number;
  name: string;
  slug: string;
  plan_id?: number | null;
  brand_color?: string | null;
  brand_logo_path?: string | null;
  locale_default?: string | null;
  timezone?: string | null;
  plan?: { id: number; name: string } | null;
}

// ---------------------------------------------------------------------------
// Step config
// ---------------------------------------------------------------------------

const STEPS = [
  { n: 1, label: 'Konto', icon: Building2 },
  { n: 2, label: 'Plan', icon: CreditCard },
  { n: 3, label: 'Branding', icon: Palette },
  { n: 4, label: 'Google Ads', icon: Link2 },
  { n: 5, label: 'Gotowe', icon: CheckCircle2 },
];

// Polish labels for plan features coming from the backend.
const FEATURE_LABELS: Record<string, string> = {
  diagnostyka: 'Diagnostyka kont',
  mutacje: 'Mutacje z akceptacją',
  chat_claude_haiku: 'Chat z Claude Haiku',
  chat_claude_opus: 'Chat z Claude Opus',
  raporty_pdf: 'Raporty PDF',
  custom_domain: 'Własna domena',
  white_label_client_panel: 'White-label panel klienta',
  multi_mc: 'Wiele kont MCC',
  four_eyes_approvals: 'Akceptacje 4-oczu',
  api_access: 'Dostęp do API',
  priority_support: 'Priorytetowe wsparcie',
};

function prettyFeature(f: string): string {
  return FEATURE_LABELS[f] ?? f.replace(/_/g, ' ');
}

function slugify(input: string): string {
  return input
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ł/g, 'l')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 60);
}

function passwordStrength(pw: string): { score: number; label: string; color: string } {
  let score = 0;
  if (pw.length >= 10) score++;
  if (pw.length >= 14) score++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  const labels = ['Za słabe', 'Słabe', 'Średnie', 'Dobre', 'Silne', 'Bardzo silne'];
  const colors = [
    'bg-slate-300', 'bg-red-500', 'bg-amber-500',
    'bg-yellow-500', 'bg-green-500', 'bg-emerald-600',
  ];
  return { score, label: labels[score], color: colors[score] };
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function RegisterAgencyPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Step survives page refresh via the ?step= query param so the OAuth
  // round-trip (which lands on /register?step=4&oauth=success) can jump
  // straight back to the right place.
  const initialStep = Math.min(5, Math.max(1, parseInt(searchParams.get('step') ?? '1', 10) || 1));
  const [step, setStep] = useState<number>(initialStep);

  // Hydrated at the end of step 1.
  const [agency, setAgency] = useState<Agency | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [importedCount, setImportedCount] = useState(0);
  const [firstAccountId, setFirstAccountId] = useState<number | null>(null);

  // Toast on oauth error bounceback.
  useEffect(() => {
    const oauth = searchParams.get('oauth');
    if (oauth === 'error') {
      const reason = searchParams.get('reason') ?? 'unknown';
      toast.error(`Nie udało się połączyć z Google Ads (${reason}).`);
    } else if (oauth === 'success') {
      toast.success('Połączono z Google Ads.');
    }
  }, [searchParams]);

  function goToStep(n: number) {
    setStep(n);
    const next = new URLSearchParams(searchParams);
    next.set('step', String(n));
    setSearchParams(next, { replace: true });
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Logo header */}
        <div className="flex items-center gap-3 mb-6 justify-center">
          <div className="h-10 w-10 rounded-md bg-brand-500 dark:bg-brand-600 flex items-center justify-center text-white font-bold">
            A
          </div>
          <div>
            <h1 className="text-xl font-semibold dark:text-white">AIAds 2.0</h1>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Rejestracja agencji — 14 dni trial
            </p>
          </div>
        </div>

        {/* Stepper */}
        <Stepper step={step} />

        {/* Step content */}
        <div className="mt-6">
          {step === 1 && (
            <Step1Account
              onComplete={(a) => {
                setAgency(a);
                goToStep(2);
              }}
            />
          )}

          {step === 2 && (
            <Step2Plan
              onBack={() => goToStep(1)}
              onComplete={(plan, updatedAgency) => {
                setSelectedPlan(plan);
                setAgency(updatedAgency);
                goToStep(3);
              }}
            />
          )}

          {step === 3 && (
            <Step3Branding
              agency={agency}
              onBack={() => goToStep(2)}
              onComplete={(updatedAgency) => {
                setAgency(updatedAgency);
                goToStep(4);
              }}
            />
          )}

          {step === 4 && (
            <Step4GoogleAds
              onBack={() => goToStep(3)}
              onComplete={(importedAccounts) => {
                setImportedCount(importedAccounts.length);
                if (importedAccounts.length > 0) {
                  setFirstAccountId(importedAccounts[0].id);
                }
                goToStep(5);
              }}
            />
          )}

          {step === 5 && (
            <Step5Summary
              agency={agency}
              plan={selectedPlan}
              importedCount={importedCount}
              firstAccountId={firstAccountId}
              onGoToAccount={(id) => navigate(`/accounts/${id}`)}
              onGoToDashboard={() => navigate('/')}
            />
          )}
        </div>

        <div className="mt-8 text-center">
          <button
            type="button"
            className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
            onClick={() => navigate('/login')}
          >
            Masz już konto? Zaloguj się
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stepper
// ---------------------------------------------------------------------------

function Stepper({ step }: { step: number }) {
  return (
    <div className="flex items-center justify-between gap-2">
      {STEPS.map((s, idx) => {
        const Icon = s.icon;
        const done = step > s.n;
        const active = step === s.n;
        return (
          <div key={s.n} className="flex items-center gap-2 flex-1">
            <div className="flex flex-col items-center gap-1">
              <div
                className={cn(
                  'h-10 w-10 rounded-full border-2 flex items-center justify-center transition',
                  done && 'bg-brand-500 border-brand-500 text-white',
                  active && 'bg-brand-50 border-brand-500 text-brand-600 dark:bg-brand-900/30',
                  !done && !active && 'bg-white border-slate-300 text-slate-400 dark:bg-slate-800 dark:border-slate-600',
                )}
              >
                {done ? <Check size={18} /> : <Icon size={18} />}
              </div>
              <span
                className={cn(
                  'text-xs font-medium hidden sm:block',
                  active ? 'text-brand-600 dark:text-brand-400' : 'text-slate-500',
                )}
              >
                {s.label}
              </span>
            </div>
            {idx < STEPS.length - 1 && (
              <div
                className={cn(
                  'flex-1 h-0.5 mb-5',
                  done ? 'bg-brand-500' : 'bg-slate-300 dark:bg-slate-700',
                )}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 1 — Konto
// ---------------------------------------------------------------------------

function Step1Account({ onComplete }: { onComplete: (agency: Agency) => void }) {
  const [form, setForm] = useState({
    agency_name: '',
    slug: '',
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
    terms_accepted: false,
  });
  const [slugDirty, setSlugDirty] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Auto-generate slug from agency name until user edits it manually.
  useEffect(() => {
    if (!slugDirty) {
      setForm((f) => ({ ...f, slug: slugify(f.agency_name) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.agency_name]);

  const strength = useMemo(() => passwordStrength(form.password), [form.password]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrors({});

    if (form.password.length < 10) {
      setErrors({ password: 'Hasło musi mieć co najmniej 10 znaków.' });
      return;
    }
    if (form.password !== form.password_confirmation) {
      setErrors({ password_confirmation: 'Hasła muszą być identyczne.' });
      return;
    }
    if (!form.terms_accepted) {
      setErrors({ terms_accepted: 'Musisz zaakceptować regulamin.' });
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post('/onboarding/register', form);
      const { agency, token, user } = res.data;
      // Persist token + user so subsequent requests are authed via the
      // existing Axios interceptor, and sync the zustand store so the
      // AuthGuard sees us as logged in when we later navigate to '/'.
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      useAuth.setState({ token, user });
      toast.success('Konto utworzone. Wybierz plan.');
      onComplete(agency);
    } catch (err: any) {
      const data = err?.response?.data;
      if (data?.errors) {
        const flat: Record<string, string> = {};
        Object.entries(data.errors).forEach(([k, v]) => {
          flat[k] = Array.isArray(v) ? (v[0] as string) : String(v);
        });
        setErrors(flat);
      }
      toast.error(data?.message ?? 'Nie udało się utworzyć konta.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="card p-6 space-y-4">
      <div>
        <h2 className="text-lg font-semibold dark:text-white">Załóż agencję</h2>
        <p className="text-sm text-slate-500 mt-1">
          Podaj podstawowe dane, aby utworzyć konto właściciela.
        </p>
      </div>

      <Field label="Nazwa agencji" error={errors.agency_name}>
        <input
          type="text"
          required
          className="input w-full dark:bg-slate-700 dark:border-slate-600 dark:text-white"
          placeholder="np. Acme Marketing Sp. z o.o."
          value={form.agency_name}
          onChange={(e) => setForm({ ...form, agency_name: e.target.value })}
        />
      </Field>

      <Field label="Identyfikator URL (slug)" error={errors.slug} hint="Używany w adresie Twojego panelu, np. acme.bdos.app">
        <div className="flex items-center gap-1">
          <input
            type="text"
            required
            className="input w-full dark:bg-slate-700 dark:border-slate-600 dark:text-white"
            placeholder="acme-marketing"
            value={form.slug}
            onChange={(e) => {
              setSlugDirty(true);
              setForm({ ...form, slug: slugify(e.target.value) });
            }}
          />
        </div>
      </Field>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Imię i nazwisko właściciela" error={errors.name}>
          <input
            type="text"
            required
            className="input w-full dark:bg-slate-700 dark:border-slate-600 dark:text-white"
            placeholder="Jan Kowalski"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </Field>

        <Field label="Email właściciela" error={errors.email}>
          <input
            type="email"
            required
            className="input w-full dark:bg-slate-700 dark:border-slate-600 dark:text-white"
            placeholder="jan@acme.pl"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </Field>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Hasło" error={errors.password} hint="Min. 10 znaków, zalecane: wielkie, małe, cyfry i znak specjalny.">
          <input
            type="password"
            required
            minLength={10}
            className="input w-full dark:bg-slate-700 dark:border-slate-600 dark:text-white"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
          {form.password.length > 0 && (
            <div className="mt-2">
              <div className="h-1 w-full bg-slate-200 rounded overflow-hidden">
                <div
                  className={cn('h-full transition-all', strength.color)}
                  style={{ width: `${Math.min(100, (strength.score / 5) * 100)}%` }}
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">Siła: {strength.label}</p>
            </div>
          )}
        </Field>

        <Field label="Powtórz hasło" error={errors.password_confirmation}>
          <input
            type="password"
            required
            minLength={10}
            className="input w-full dark:bg-slate-700 dark:border-slate-600 dark:text-white"
            value={form.password_confirmation}
            onChange={(e) => setForm({ ...form, password_confirmation: e.target.value })}
          />
        </Field>
      </div>

      <label className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300">
        <input
          type="checkbox"
          className="mt-1"
          checked={form.terms_accepted}
          onChange={(e) => setForm({ ...form, terms_accepted: e.target.checked })}
        />
        <span>
          Akceptuję{' '}
          <a href="/regulamin" target="_blank" rel="noreferrer" className="text-brand-600 underline">
            regulamin
          </a>{' '}
          oraz{' '}
          <a href="/polityka-prywatnosci" target="_blank" rel="noreferrer" className="text-brand-600 underline">
            politykę prywatności
          </a>
          .
        </span>
      </label>
      {errors.terms_accepted && (
        <p className="text-xs text-red-500 -mt-2">{errors.terms_accepted}</p>
      )}

      <div className="flex justify-end pt-2">
        <button
          type="submit"
          disabled={submitting}
          className="btn-primary flex items-center gap-2"
        >
          {submitting && <Loader2 size={16} className="animate-spin" />}
          Utwórz konto
          <ChevronRight size={16} />
        </button>
      </div>
    </form>
  );
}

function Field({
  label, children, error, hint,
}: {
  label: string;
  children: React.ReactNode;
  error?: string;
  hint?: string;
}) {
  return (
    <div>
      <label className="text-sm font-medium text-slate-700 dark:text-slate-300 block mb-1">
        {label}
      </label>
      {children}
      {hint && !error && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — Plan
// ---------------------------------------------------------------------------

function Step2Plan({
  onBack,
  onComplete,
}: {
  onBack: () => void;
  onComplete: (plan: Plan, agency: Agency) => void;
}) {
  const [submitting, setSubmitting] = useState<number | null>(null);

  const { data: plans = [], isLoading, error } = useQuery<Plan[]>({
    queryKey: ['onboarding', 'plans'],
    queryFn: async () => {
      const res = await api.get('/billing/plans');
      return res.data.data;
    },
  });

  async function choose(plan: Plan) {
    setSubmitting(plan.id);
    try {
      const res = await api.post('/onboarding/plan', { plan_id: plan.id });
      toast.success(`Plan ${plan.name} aktywowany (trial 14 dni).`);
      onComplete(plan, res.data.agency);
    } catch {
      toast.error('Nie udało się aktywować planu.');
    } finally {
      setSubmitting(null);
    }
  }

  const recommendedCode = 'pro';

  return (
    <div className="card p-6 space-y-6">
      <div>
        <h2 className="text-lg font-semibold dark:text-white">Wybierz plan</h2>
        <p className="text-sm text-slate-500 mt-1">
          Pierwsze 14 dni jest bezpłatne dla każdego planu. Nie wymagamy karty.
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600">
          <AlertTriangle size={16} />
          Nie udało się pobrać listy planów.
        </div>
      )}

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-80 rounded-xl bg-slate-100 dark:bg-slate-800 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {plans.map((plan) => {
            const recommended = plan.slug === recommendedCode;
            const pending = submitting === plan.id;
            return (
              <div
                key={plan.id}
                className={cn(
                  'rounded-xl border-2 p-5 flex flex-col transition',
                  recommended
                    ? 'border-brand-500 shadow-lg bg-brand-50/30 dark:bg-brand-900/10'
                    : 'border-slate-200 dark:border-slate-700',
                )}
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold dark:text-white">{plan.name}</h3>
                  <Badge variant="info">14 dni trial</Badge>
                </div>
                {recommended && (
                  <Badge variant="success" className="self-start mb-2">
                    Polecany
                  </Badge>
                )}
                <div className="mt-2 mb-4">
                  <span className="text-3xl font-bold dark:text-white">
                    {new Intl.NumberFormat('pl-PL', {
                      style: 'currency',
                      currency: plan.currency ?? 'PLN',
                      maximumFractionDigits: 0,
                    }).format(plan.price_monthly)}
                  </span>
                  <span className="text-sm text-slate-500"> / mies.</span>
                </div>

                <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-300 mb-6 flex-1">
                  {plan.max_accounts != null && (
                    <li className="flex items-start gap-2">
                      <Check size={16} className="text-brand-500 mt-0.5" />
                      Do {plan.max_accounts} kont Google Ads
                    </li>
                  )}
                  {plan.max_users != null && (
                    <li className="flex items-start gap-2">
                      <Check size={16} className="text-brand-500 mt-0.5" />
                      Do {plan.max_users} użytkowników
                    </li>
                  )}
                  {plan.max_claude_tokens_per_month != null && (
                    <li className="flex items-start gap-2">
                      <Check size={16} className="text-brand-500 mt-0.5" />
                      {Math.round(plan.max_claude_tokens_per_month / 1000).toLocaleString('pl-PL')}k tokenów Claude/mies.
                    </li>
                  )}
                  {(plan.features ?? []).slice(0, 6).map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check size={16} className="text-brand-500 mt-0.5" />
                      {prettyFeature(f)}
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  disabled={pending}
                  onClick={() => choose(plan)}
                  className={cn(
                    'w-full flex items-center justify-center gap-2 rounded-md py-2 font-medium transition',
                    recommended
                      ? 'bg-brand-500 text-white hover:bg-brand-600'
                      : 'border-2 border-brand-500 text-brand-600 hover:bg-brand-50',
                  )}
                >
                  {pending && <Loader2 size={16} className="animate-spin" />}
                  Wybierz
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex justify-between pt-2">
        <button type="button" onClick={onBack} className="btn-secondary flex items-center gap-2">
          <ChevronLeft size={16} /> Wstecz
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 3 — Branding
// ---------------------------------------------------------------------------

function Step3Branding({
  agency,
  onBack,
  onComplete,
}: {
  agency: Agency | null;
  onBack: () => void;
  onComplete: (agency: Agency) => void;
}) {
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [brandColor, setBrandColor] = useState(agency?.brand_color ?? '#2E5BFF');
  const [locale, setLocale] = useState(agency?.locale_default ?? 'pl');
  const [timezone, setTimezone] = useState(agency?.timezone ?? 'Europe/Warsaw');
  const [submitting, setSubmitting] = useState(false);

  function handleLogoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024) {
      toast.error('Logo za duże — maksymalnie 1 MB.');
      return;
    }
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const form = new FormData();
      if (logoFile) form.append('logo', logoFile);
      form.append('brand_color', brandColor);
      form.append('locale_default', locale);
      form.append('timezone', timezone);
      const res = await api.post('/onboarding/branding', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Ustawienia brandingu zapisane.');
      onComplete(res.data.agency);
    } catch {
      toast.error('Nie udało się zapisać brandingu.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="card p-6 space-y-6">
      <div>
        <h2 className="text-lg font-semibold dark:text-white">Branding agencji</h2>
        <p className="text-sm text-slate-500 mt-1">
          Wszystko można zmienić później w ustawieniach agencji.
        </p>
      </div>

      {/* Logo upload */}
      <div>
        <label className="text-sm font-medium text-slate-700 dark:text-slate-300 block mb-2">
          Logo agencji
        </label>
        <div className="flex items-center gap-4">
          <div className="h-20 w-20 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 flex items-center justify-center overflow-hidden bg-slate-50 dark:bg-slate-800">
            {logoPreview ? (
              // eslint-disable-next-line jsx-a11y/img-redundant-alt
              <img src={logoPreview} alt="Logo" className="h-full w-full object-contain" />
            ) : (
              <Upload size={24} className="text-slate-400" />
            )}
          </div>
          <div>
            <input
              id="logo-upload"
              type="file"
              accept="image/png,image/jpeg,image/svg+xml"
              className="hidden"
              onChange={handleLogoChange}
            />
            <label htmlFor="logo-upload" className="btn-secondary cursor-pointer inline-flex items-center gap-2">
              <Upload size={16} />
              Wybierz plik
            </label>
            <p className="text-xs text-slate-500 mt-1">PNG, JPG lub SVG, maks. 1 MB.</p>
          </div>
        </div>
      </div>

      {/* Brand color */}
      <div>
        <label className="text-sm font-medium text-slate-700 dark:text-slate-300 block mb-2">
          Kolor marki
        </label>
        <div className="flex items-center gap-3">
          <input
            type="color"
            value={brandColor}
            onChange={(e) => setBrandColor(e.target.value)}
            className="h-10 w-16 rounded cursor-pointer border border-slate-300"
          />
          <input
            type="text"
            value={brandColor}
            onChange={(e) => setBrandColor(e.target.value)}
            pattern="^#[0-9A-Fa-f]{6}$"
            className="input w-32 font-mono dark:bg-slate-700 dark:border-slate-600 dark:text-white"
          />
          <div
            className="h-10 flex-1 rounded flex items-center justify-center text-white font-medium"
            style={{ backgroundColor: brandColor }}
          >
            Podgląd
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300 block mb-1">
            Język domyślny
          </label>
          <select
            value={locale}
            onChange={(e) => setLocale(e.target.value)}
            className="input w-full dark:bg-slate-700 dark:border-slate-600 dark:text-white"
          >
            <option value="pl">Polski (PL)</option>
            <option value="en">English (EN)</option>
            <option value="de">Deutsch (DE)</option>
            <option value="uk">Українська (UK)</option>
          </select>
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300 block mb-1">
            Strefa czasowa
          </label>
          <select
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            className="input w-full dark:bg-slate-700 dark:border-slate-600 dark:text-white"
          >
            <option value="Europe/Warsaw">Europe/Warsaw</option>
            <option value="Europe/Berlin">Europe/Berlin</option>
            <option value="Europe/London">Europe/London</option>
            <option value="Europe/Kyiv">Europe/Kyiv</option>
            <option value="UTC">UTC</option>
          </select>
        </div>
      </div>

      <div className="flex justify-between pt-2">
        <button type="button" onClick={onBack} className="btn-secondary flex items-center gap-2">
          <ChevronLeft size={16} /> Wstecz
        </button>
        <button type="submit" disabled={submitting} className="btn-primary flex items-center gap-2">
          {submitting && <Loader2 size={16} className="animate-spin" />}
          Dalej
          <ChevronRight size={16} />
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Step 4 — Google Ads OAuth
// ---------------------------------------------------------------------------

function Step4GoogleAds({
  onBack,
  onComplete,
}: {
  onBack: () => void;
  onComplete: (accounts: { id: number; customer_id: string }[]) => void;
}) {
  const [connecting, setConnecting] = useState(false);
  const [status, setStatus] = useState<OauthStatus | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState(false);
  const [polling, setPolling] = useState(false);

  async function fetchStatus() {
    try {
      const res = await api.get<OauthStatus>('/onboarding/oauth-status');
      setStatus(res.data);
      return res.data;
    } catch {
      return null;
    }
  }

  // Initial status check on mount (handles the post-OAuth redirect)
  useEffect(() => {
    fetchStatus();
  }, []);

  // Poll every 2s while we're actively waiting for the callback.
  useEffect(() => {
    if (!polling) return;
    const h = setInterval(async () => {
      const s = await fetchStatus();
      if (s?.connected) {
        setPolling(false);
      }
    }, 2000);
    return () => clearInterval(h);
  }, [polling]);

  async function handleConnect() {
    setConnecting(true);
    try {
      const res = await api.get<{ authorize_url: string }>('/onboarding/oauth-url');
      setPolling(true);
      // Full-page redirect — cleanest way to preserve the Google state param
      // and land back on /register?step=4 after the backend redirects us.
      window.location.href = res.data.authorize_url;
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? 'Nie udało się zainicjować OAuth.');
      setConnecting(false);
      setPolling(false);
    }
  }

  function toggleCustomer(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleImport() {
    if (selected.size === 0) {
      toast.error('Zaznacz co najmniej jedno konto do importu.');
      return;
    }
    setImporting(true);
    try {
      const res = await api.post('/onboarding/import-accounts', {
        customer_ids: Array.from(selected),
      });
      toast.success(`Zaimportowano ${res.data.imported} kont.`);
      onComplete(res.data.accounts ?? []);
    } catch {
      toast.error('Nie udało się zaimportować kont.');
    } finally {
      setImporting(false);
    }
  }

  const connected = status?.connected ?? false;
  const customers = status?.accessible_customers ?? [];

  return (
    <div className="card p-6 space-y-6">
      <div>
        <h2 className="text-lg font-semibold dark:text-white">Połącz Google Ads</h2>
        <p className="text-sm text-slate-500 mt-1">
          Potrzebujemy uprawnień do Twoich kont Google Ads, żeby zbierać dane i proponować zmiany.
        </p>
      </div>

      {!connected ? (
        <div className="rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-700 p-8 text-center space-y-4">
          <div className="h-16 w-16 mx-auto rounded-full bg-brand-50 dark:bg-brand-900/30 flex items-center justify-center">
            <Link2 size={28} className="text-brand-500" />
          </div>
          <div>
            <p className="text-sm font-medium dark:text-white">Brak połączenia</p>
            <p className="text-xs text-slate-500 mt-1">
              Klik poniżej przeniesie Cię na ekran zgody Google.
            </p>
          </div>
          <button
            type="button"
            onClick={handleConnect}
            disabled={connecting}
            className="btn-primary inline-flex items-center gap-2"
          >
            {connecting && <Loader2 size={16} className="animate-spin" />}
            Połącz konto Google Ads
          </button>
          {polling && (
            <p className="text-xs text-slate-500 mt-2">
              Oczekiwanie na autoryzację…
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
              <CheckCircle2 size={18} />
              <span className="text-sm font-medium">Połączono z Google Ads</span>
            </div>
            <button
              type="button"
              onClick={fetchStatus}
              className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
            >
              <RefreshCw size={12} /> Odśwież
            </button>
          </div>

          <div>
            <p className="text-sm font-medium dark:text-white mb-2">
              Dostępne konta ({customers.length})
            </p>
            {customers.length === 0 ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/20 p-4 text-sm text-amber-700 dark:text-amber-300">
                <div className="flex items-start gap-2">
                  <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                  <div>
                    Nie znaleźliśmy kont Google Ads powiązanych z Twoim loginem. Upewnij się,
                    że masz dostęp do przynajmniej jednego konta lub dodaj je ręcznie po zakończeniu onboardingu.
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {customers.map((c) => {
                  const isSelected = selected.has(c.customer_id);
                  return (
                    <label
                      key={c.customer_id}
                      className={cn(
                        'flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition',
                        isSelected
                          ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20'
                          : 'border-slate-200 dark:border-slate-700 hover:border-slate-300',
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleCustomer(c.customer_id)}
                      />
                      <Sparkles size={16} className="text-brand-500" />
                      <span className="font-mono text-sm dark:text-white">{c.customer_id}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex justify-between pt-2">
        <button type="button" onClick={onBack} className="btn-secondary flex items-center gap-2">
          <ChevronLeft size={16} /> Wstecz
        </button>
        {connected && customers.length > 0 ? (
          <button
            type="button"
            onClick={handleImport}
            disabled={importing || selected.size === 0}
            className="btn-primary flex items-center gap-2"
          >
            {importing && <Loader2 size={16} className="animate-spin" />}
            Importuj wybrane ({selected.size})
            <ChevronRight size={16} />
          </button>
        ) : connected ? (
          <button
            type="button"
            onClick={() => onComplete([])}
            className="btn-secondary flex items-center gap-2"
          >
            Pomiń import
            <ChevronRight size={16} />
          </button>
        ) : (
          <button
            type="button"
            onClick={() => onComplete([])}
            className="text-sm text-slate-500 hover:text-slate-700"
          >
            Pomiń na razie
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 5 — Summary
// ---------------------------------------------------------------------------

function Step5Summary({
  agency,
  plan,
  importedCount,
  firstAccountId,
  onGoToAccount,
  onGoToDashboard,
}: {
  agency: Agency | null;
  plan: Plan | null;
  importedCount: number;
  firstAccountId: number | null;
  onGoToAccount: (id: number) => void;
  onGoToDashboard: () => void;
}) {
  const [completing, setCompleting] = useState(false);
  const [completed, setCompleted] = useState(false);

  // Auto-mark as completed on mount.
  useEffect(() => {
    (async () => {
      setCompleting(true);
      try {
        await api.post('/onboarding/complete');
        setCompleted(true);
      } catch {
        toast.error('Nie udało się zakończyć onboardingu.');
      } finally {
        setCompleting(false);
      }
    })();
  }, []);

  return (
    <div className="card p-6 space-y-6 text-center">
      <div className="h-16 w-16 mx-auto rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
        {completing ? (
          <Loader2 size={28} className="text-green-600 animate-spin" />
        ) : (
          <CheckCircle2 size={28} className="text-green-600" />
        )}
      </div>

      <div>
        <h2 className="text-2xl font-semibold dark:text-white">Gotowe!</h2>
        <p className="text-sm text-slate-500 mt-2">
          Agencja <strong>{agency?.name ?? '—'}</strong> została utworzona.
          Plan <strong>{plan?.name ?? '—'}</strong> jest aktywny (14 dni trial).
        </p>
      </div>

      <div className="mx-auto max-w-sm space-y-2 text-sm text-slate-600 dark:text-slate-300">
        <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 py-2">
          <span>Slug agencji</span>
          <span className="font-mono">{agency?.slug ?? '—'}</span>
        </div>
        <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 py-2">
          <span>Plan</span>
          <span>{plan?.name ?? '—'}</span>
        </div>
        <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 py-2">
          <span>Zaimportowane konta</span>
          <span>{importedCount}</span>
        </div>
        <div className="flex items-center justify-between py-2">
          <span>Trial do</span>
          <span>
            {new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toLocaleDateString('pl-PL')}
          </span>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
        {firstAccountId && (
          <button
            type="button"
            onClick={() => onGoToAccount(firstAccountId)}
            disabled={!completed}
            className="btn-primary inline-flex items-center gap-2"
          >
            Idź do pierwszego konta
            <ChevronRight size={16} />
          </button>
        )}
        <button
          type="button"
          onClick={onGoToDashboard}
          disabled={!completed}
          className={cn(
            firstAccountId ? 'btn-secondary' : 'btn-primary',
            'inline-flex items-center gap-2',
          )}
        >
          Zobacz dashboard
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}
